import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { getUser } from "@/lib/api/auth"
import {
  patchUserName,
  patchUserEmail,
  patchWantsNews,
  patchWantsQuestProgress,
  patchWantsReminders,
} from "@/lib/api/circle"

export const useUser = () => {
  const queryClient = useQueryClient()

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user"],
    queryFn: getUser,
    retry: false,
  })

  const { mutate: updateName, isPending: isUpdatingName } = useMutation({
    mutationFn: ({ fullName }: { fullName: string }) => patchUserName(fullName),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["user"], updatedUser)
    },
  })

  const { mutate: updateWantsNews, isPending: isUpdatingWantsNews } =
    useMutation({
      mutationFn: ({ wantsNews }: { wantsNews: boolean }) =>
        patchWantsNews(wantsNews),
      onSuccess: (updatedUser) => {
        queryClient.setQueryData(["user"], updatedUser)
      },
    })

  const {
    mutate: updateWantsQuestProgress,
    isPending: isUpdatingWantsQuestProgress,
  } = useMutation({
    mutationFn: ({ wantsQuestProgress }: { wantsQuestProgress: boolean }) =>
      patchWantsQuestProgress(wantsQuestProgress),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["user"], updatedUser)
    },
  })

  const { mutate: updateWantsReminders, isPending: isUpdatingWantsReminders } =
    useMutation({
      mutationFn: ({ wantsReminders }: { wantsReminders: boolean }) =>
        patchWantsReminders(wantsReminders),
      onSuccess: (updatedUser) => {
        queryClient.setQueryData(["user"], updatedUser)
      },
    })

  const { mutate: updateEmail, isPending: isUpdatingEmail } = useMutation({
    mutationFn: ({ email, profileId }: { email: string; profileId: string }) =>
      patchUserEmail(email, profileId),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(["user"], updatedUser)
    },
  })

  return {
    user,
    isLoading,
    error,
    updateName,
    isUpdatingName,
    updateEmail,
    isUpdatingEmail,
    updateWantsNews,
    isUpdatingWantsNews,
    updateWantsQuestProgress,
    isUpdatingWantsQuestProgress,
    updateWantsReminders,
    isUpdatingWantsReminders,
  }
}
